"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import Link from "next/link";
import { Settings, X, Check } from "lucide-react";
import { useConsent, type ConsentPreferences } from "@/lib/consent";
import { strings } from "@/lib/strings";
import { useIsSlimConsent } from "@/components/providers";
import { cn } from "@/lib/utils";
interface CookieConsentBannerProps {
  /** Reserve the slim strip's actual height above pages with their own static header. */
  inFlow?: boolean;
}

/**
 * Cookie consent banner for GDPR compliance.
 * - Default: bottom sheet with settings link.
 * - Slim variant (funnel + post-funnel pages that render FunnelHeader): slim
 *   top strip, non-occluding over a bottom-anchored primary CTA. Opted in via
 *   useSlimConsent() — typically from FunnelHeader — so the decision lives with
 *   the layout, not a pathname regex (EXP-007 / HYP-009 A3).
 */
export function CookieConsentBanner(t0) {
  const $ = _c(31);
  const {
    inFlow: t1
  } = t0;
  const inFlow = t1 === undefined ? false : t1;
  const {
    showBanner,
    preferences,
    acceptAll,
    acceptNecessary,
    updatePreferences
  } = useConsent();
  const slimVariant = useIsSlimConsent();
  const [showSettings, setShowSettings] = useState(false);
  const [localPreferences, setLocalPreferences] = useState(preferences);
  if (preferences !== localPreferences && !showSettings) {
    setLocalPreferences(preferences);
  }
  if (!showBanner) {
    return null;
  }
  let t2;
  if ($[0] !== localPreferences || $[1] !== updatePreferences) {
    t2 = () => {
      updatePreferences(localPreferences);
      setShowSettings(false);
    };
    $[0] = localPreferences;
    $[1] = updatePreferences;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleSaveSettings = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = category => {
      setLocalPreferences(prev => ({
        ...prev,
        [category]: !prev[category]
      }));
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const handleToggleCategory = t3;
  if (slimVariant && !showSettings) {
    const t4 = inFlow ? "sticky" : "fixed";
    let t5;
    if ($[4] !== t4) {
      t5 = cn("inset-x-0 top-0 z-[51] animate-in slide-in-from-top duration-300", t4);
      $[4] = t4;
      $[5] = t5;
    } else {
      t5 = $[5];
    }
    let t6;
    let t7;
    let t8;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <span className="hidden sm:inline min-w-0 flex-1 truncate">🍪 {strings.consent.bannerTitle}</span>;
      t7 = <span className="sm:hidden min-w-0 flex-1 truncate">🍪 Cookies</span>;
      t8 = () => setShowSettings(true);
      $[6] = t6;
      $[7] = t7;
      $[8] = t8;
    } else {
      t6 = $[6];
      t7 = $[7];
      t8 = $[8];
    }
    let t9;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <button onClick={t8} aria-label={strings.consent.manageSettings} className="flex-shrink-0 px-2 py-1 text-xs text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 rounded transition-colors"><Settings className="w-4 h-4 sm:hidden" aria-hidden="true" /><span className="hidden sm:inline">{strings.consent.manageSettings}</span></button>;
      $[9] = t9;
    } else {
      t9 = $[9];
    }
    let t10;
    if ($[10] !== acceptNecessary) {
      t10 = <button onClick={acceptNecessary} className="flex-shrink-0 px-2 sm:px-3 py-1 text-xs font-medium text-zinc-700 bg-zinc-100 hover:bg-zinc-200 rounded transition-colors">{strings.consent.acceptNecessary}</button>;
      $[10] = acceptNecessary;
      $[11] = t10;
    } else {
      t10 = $[11];
    }
    let t11;
    if ($[12] !== acceptAll) {
      t11 = <button onClick={acceptAll} className="flex-shrink-0 px-2 sm:px-3 py-1 text-xs font-medium text-white rounded transition-opacity hover:opacity-90 bg-tenant-primary">{strings.consent.acceptAll}</button>;
      $[12] = acceptAll;
      $[13] = t11;
    } else {
      t11 = $[13];
    }
    let t12;
    if ($[14] !== t10 || $[15] !== t11) {
      t12 = <div className="bg-white border-b border-zinc-200 shadow-sm"><div className="container-wide py-2 flex items-center gap-2 sm:gap-3 text-xs text-zinc-600">{t6}{t7}{t9}{t10}{t11}</div></div>;
      $[14] = t10;
      $[15] = t11;
      $[16] = t12;
    } else {
      t12 = $[16];
    }
    let t13;
    if ($[17] !== t12 || $[18] !== t5) {
      t13 = <div className={t5}>{t12}</div>;
      $[17] = t12;
      $[18] = t5;
      $[19] = t13;
    } else {
      t13 = $[19];
    }
    return t13;
  }
  let t4;
  if ($[20] !== showSettings) {
    t4 = showSettings && <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setShowSettings(false)} />;
    $[20] = showSettings;
    $[21] = t4;
  } else {
    t4 = $[21];
  }
  let t5;
  if ($[22] !== acceptAll || $[23] !== acceptNecessary || $[24] !== handleSaveSettings || $[25] !== localPreferences || $[26] !== showSettings) {
    t5 = <div className="relative bg-white border-t border-zinc-200 shadow-lg"><div className="container-wide py-4">{!showSettings ? <div className="flex flex-col md:flex-row md:items-center gap-4"><div className="flex-1"><h3 className="font-medium text-zinc-900 mb-1">{strings.consent.bannerTitle}</h3><p className="text-sm text-zinc-600">{strings.consent.bannerDescription}{" "}<Link href="/datenschutz" className="underline hover:text-zinc-900">{strings.consent.privacyPolicy}</Link></p></div><div className="flex flex-wrap items-center gap-2"><button onClick={() => setShowSettings(true)} className="flex items-center gap-2 px-4 py-2 text-sm text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 rounded-lg transition-colors"><Settings className="w-4 h-4" />{strings.consent.manageSettings}</button><button onClick={acceptNecessary} className="px-4 py-2 text-sm font-medium text-zinc-700 bg-zinc-100 hover:bg-zinc-200 rounded-lg transition-colors">{strings.consent.acceptNecessary}</button><button onClick={acceptAll} className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-opacity hover:opacity-90 bg-tenant-primary">{strings.consent.acceptAll}</button></div></div> : <div className="space-y-4"><div className="flex items-center justify-between"><h3 className="font-medium text-zinc-900">{strings.consent.settingsTitle}</h3><button onClick={() => setShowSettings(false)} className="p-1 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded-lg transition-colors"><X className="w-5 h-5" /></button></div><div className="space-y-3 max-h-[50vh] overflow-y-auto"><div className="flex items-start gap-4 p-3 bg-zinc-50 rounded-lg"><div className="flex-1 min-w-0"><div className="flex items-center gap-2"><span className="font-medium text-zinc-900">{strings.consent.categories.necessary}</span><span className="text-xs text-zinc-500 bg-zinc-200 px-2 py-0.5 rounded">{strings.consent.alwaysActive}</span></div><p className="text-sm text-zinc-600 mt-1">{strings.consent.categories.necessaryDescription}</p></div><div className="flex-shrink-0 w-10 h-6 bg-zinc-300 rounded-full flex items-center justify-end px-0.5 cursor-not-allowed opacity-60" title={strings.consent.alwaysActive}><div className="w-5 h-5 bg-white rounded-full shadow flex items-center justify-center"><Check className="w-3 h-3 text-zinc-500" /></div></div></div><div className="flex items-start gap-4 p-3 bg-zinc-50 rounded-lg"><div className="flex-1 min-w-0"><span className="font-medium text-zinc-900">{strings.consent.categories.analytics}</span><p className="text-sm text-zinc-600 mt-1">{strings.consent.categories.analyticsDescription}</p></div><button onClick={() => handleToggleCategory("analytics")} className={`flex-shrink-0 w-10 h-6 rounded-full flex items-center px-0.5 transition-colors ${localPreferences.analytics ? "justify-end bg-tenant-primary" : "justify-start bg-zinc-300"}`}><div className="w-5 h-5 bg-white rounded-full shadow flex items-center justify-center">{localPreferences.analytics && <Check className="w-3 h-3 text-tenant-primary" />}</div></button></div><div className="flex items-start gap-4 p-3 bg-zinc-50 rounded-lg"><div className="flex-1 min-w-0"><span className="font-medium text-zinc-900">{strings.consent.categories.marketing}</span><p className="text-sm text-zinc-600 mt-1">{strings.consent.categories.marketingDescription}</p></div><button onClick={() => handleToggleCategory("marketing")} className={`flex-shrink-0 w-10 h-6 rounded-full flex items-center px-0.5 transition-colors ${localPreferences.marketing ? "justify-end bg-tenant-primary" : "justify-start bg-zinc-300"}`}><div className="w-5 h-5 bg-white rounded-full shadow flex items-center justify-center">{localPreferences.marketing && <Check className="w-3 h-3 text-tenant-primary" />}</div></button></div></div><div className="flex flex-wrap items-center justify-end gap-2 pt-2 border-t border-zinc-200"><button onClick={acceptNecessary} className="px-4 py-2 text-sm font-medium text-zinc-700 bg-zinc-100 hover:bg-zinc-200 rounded-lg transition-colors">{strings.consent.acceptNecessary}</button><button onClick={handleSaveSettings} className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-opacity hover:opacity-90 bg-tenant-primary">{strings.consent.saveSettings}</button></div></div>}</div></div>;
    $[22] = acceptAll;
    $[23] = acceptNecessary;
    $[24] = handleSaveSettings;
    $[25] = localPreferences;
    $[26] = showSettings;
    $[27] = t5;
  } else {
    t5 = $[27];
  }
  let t6;
  if ($[28] !== t4 || $[29] !== t5) {
    t6 = <div className="fixed inset-x-0 bottom-0 z-50 animate-in slide-in-from-bottom duration-300">{t4}{t5}</div>;
    $[28] = t4;
    $[29] = t5;
    $[30] = t6;
  } else {
    t6 = $[30];
  }
  return t6;
}